import { Link } from 'react-router-dom';
import { Badge, Button } from '@dreamtree-org/twreact-ui';
import { Money } from '@/components';
import { GRADE_VARIANT, batteryShown, conditionBadge, discountPct, gradeApplies, specLine } from '../lib/listing';
import UnitImage from './UnitImage';
import QtySelect from './QtySelect';
import { isQtyItem, maxQtyOf, qtyOf } from '../lib/cart';
import { useStorefront } from '../StorefrontContext';

/**
 * One cart, checkout or order line: an exact unit (qty 1), or an item sold by
 * quantity (D60) — price each × qty, with a picker when onQty is given.
 */
export default function CartLineItem({ item, onRemove, removing, onQty, compact = false }) {
  const sf = useStorefront();
  // The line's product type (from the registry) shapes its spec line; none known, no line.
  const type = (sf && sf.shop && sf.shop.typeOf(item)) || null;
  const off = discountPct(item);
  const qty = qtyOf(item);
  const byQty = isQtyItem(item) || qty > 1;
  const name = [item.brand, item.series, item.model_name].filter(Boolean).join(' ') || item.title;
  // order lines are snapshots without a live listing; link only while the listing exists
  const href = item.listing_id ? `/p/${item.listing_id}` : null;
  const Wrap = ({ className, children }) => (href ? <Link to={href} className={className}>{children}</Link> : <span className={className}>{children}</span>);
  return (
    <div className="flex gap-3 py-3">
      <Wrap className="block w-20 shrink-0 sm:w-28">
        <UnitImage size="sm" className="aspect-square" label={item.title} icon={(type || {}).icon} />
      </Wrap>
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-start justify-between gap-x-3">
          <Wrap className="min-w-0 font-semibold leading-snug hover:underline">{name}</Wrap>
          <div className="text-right">
            <Money paise={item.price_paise * qty} className="font-bold" />
            {byQty && qty > 1 ? <div className="text-xs opacity-70">{qty} × <Money paise={item.price_paise} /></div> : null}
            {off && !byQty ? <div className="text-xs"><span className="line-through opacity-50"><Money paise={item.mrp_paise} /></span> <span className="text-success-700">{off}% off</span></div> : null}
          </div>
        </div>
        {item.variant_label ? <div className="text-sm font-medium" data-testid="line-variant">{item.variant_label}</div> : null}
        <div className="text-xs opacity-70 sm:text-sm">{specLine(item, type)}</div>
        {item.available === false ? (
          <Badge size="xs" variant="error">Unavailable: {item.unavailable_reason || 'no longer listed'}. Remove it to check out.</Badge>
        ) : null}
        <div className="flex flex-wrap gap-1.5">
          {/* The condition shows on every line, compact ones too (D98). */}
          <span data-testid="line-condition"><Badge size="xs" variant="success">{conditionBadge(item)}</Badge></span>
          {!compact && !byQty && gradeApplies(item) && item.grade ? <Badge size="xs" variant={GRADE_VARIANT[item.grade] || 'default'}>Grade {item.grade}</Badge> : null}
          {!compact && batteryShown(item) ? <Badge size="xs" variant="outline">Battery {item.battery_health_pct}%</Badge> : null}
          {!compact && item.warranty_months ? <Badge size="xs" variant="outline">{item.warranty_months} mo warranty</Badge> : null}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs opacity-80">
          {byQty && onQty ? (
            <div className="w-24"><QtySelect size="sm" label="Qty" value={qty} max={Math.max(qty, maxQtyOf(item))} onChange={onQty} disabled={removing} /></div>
          ) : (
            <span>{byQty ? `Qty ${qty}` : `Serial ${item.serial_masked || 'not listed'} · Qty 1 (unique unit)`}</span>
          )}
          {onRemove ? <Button size="xs" variant="ghost" loading={removing} onClick={onRemove}>Remove</Button> : null}
        </div>
      </div>
    </div>
  );
}
